import { FoodsIcon } from "@foodpilot/foods";
import { Box, Button, CircularProgress, Dialog, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";

// Feature Deactivation Confirmation Modal Component
export const FeatureDeactivationModal = ({
    open,
    onClose,
    onConfirm,
    isDeactivating,
    featureName,
}: {
    open: boolean;
    onClose: () => void;
    onConfirm: () => void;
    isDeactivating: boolean;
    featureName: string;
}) => {
    const { t } = useTranslation();
    const theme = useTheme();

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: "16px",
                    overflow: "hidden",
                },
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "32px",
                    borderBottom: "1px solid #E7E6E4",
                    backgroundColor: "white",
                }}
            >
                <Box>
                    <Typography variant="h3" sx={{ lineHeight: "1.6em", color: "#1E1E1E", marginBottom: "8px" }}>
                        {t("settings.feature.deactivate.title", "Désactiver la fonctionnalité")}
                    </Typography>
                    <Typography variant="small-medium" sx={{ lineHeight: "1.6em", color: "#50504D" }}>
                        {t(
                            "settings.feature.deactivate.subtitle",
                            "Cette action peut affecter l'expérience utilisateur.",
                        )}
                    </Typography>
                </Box>
                <Box
                    onClick={onClose}
                    sx={{
                        width: "40px",
                        height: "40px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        borderRadius: "50%",
                        "&:hover": {
                            backgroundColor: "#F5F5F5",
                        },
                    }}
                >
                    <FoodsIcon icon="close" size={1} />
                </Box>
            </Box>

            {/* Content */}
            <Box
                sx={{
                    padding: "32px",
                    backgroundColor: "#FEFCFB",
                }}
            >
                <Typography variant="small-medium" sx={{ lineHeight: "1.6em", color: "#393A36", textAlign: "center" }}>
                    {t(
                        "settings.feature.deactivate.warning",
                        "Êtes-vous sûr de vouloir désactiver la fonctionnalité « {{featureName}} » ?",
                        { featureName },
                    )}
                </Typography>
            </Box>

            {/* Footer */}
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                    gap: "12px",
                    padding: "0px 24px 0px 32px",
                    height: "72px",
                    backgroundColor: "white",
                    borderTop: "1px solid #E7E6E4",
                }}
            >
                <Button
                    onClick={onClose}
                    disabled={isDeactivating}
                    sx={{
                        width: "100px",
                        height: "40px",
                        borderRadius: "44px",
                        textTransform: "none",
                        fontWeight: 700,
                        backgroundColor: "transparent",
                        color: "#393A36",
                        border: "1px solid #E7E6E4",
                        "&:hover": {
                            backgroundColor: "#F5F5F5",
                        },
                    }}
                >
                    {t("settings.feature.cancel", "Annuler")}
                </Button>
                <Button
                    onClick={onConfirm}
                    disabled={isDeactivating}
                    sx={{
                        width: "120px",
                        height: "40px",
                        borderRadius: "44px",
                        textTransform: "none",
                        fontWeight: 700,
                        background: `linear-gradient(180deg, ${theme.palette.primary[500]} 0%, ${theme.palette.primary[600]} 100%)`,
                        color: "white",
                        "&:hover": {
                            background: `linear-gradient(180deg, ${theme.palette.primary[600]} 0%, ${theme.palette.primary[700]} 100%)`,
                        },
                        "&:disabled": {
                            background: `linear-gradient(180deg, ${theme.palette.primary[500]} 0%, ${theme.palette.primary[600]} 100%)`,
                            color: "white",
                            opacity: 0.6,
                        },
                    }}
                >
                    {isDeactivating ?
                        <CircularProgress size={20} color="inherit" />
                    :   t("settings.feature.deactivate.confirm", "Désactiver")}
                </Button>
            </Box>
        </Dialog>
    );
};
